"use client";

// One place that sends a signed-in recruiter to the Stripe Pro checkout and
// refreshes the plan when Stripe sends them back. Used by the pricing card, the
// sidebar button and the profile plan card, so all three behave the same.

import { useCallback, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuthState } from "react-firebase-hooks/auth";

import { auth } from "@/components/context/firebase";
import { AppStateContext } from "@/components/context/AppContext";
import { t as translate } from "@/lib/i18n";
import { API_URL } from "@/components/config/config";
import fetchWithAuth from "@/hooks/fetchWithAuth";
import {
  finishCheckoutReturn,
  startProCheckout,
  stripeEnabled,
  type UpgradePlacement,
} from "@/lib/stripeCheckout";

export function useProUpgrade() {
  const [user] = useAuthState(auth);
  const { setUserProfile } = useContext(AppStateContext);
  const [busy, setBusy] = useState(false);

  const refreshProfile = useCallback(async (uid: string) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/profiles/${uid}/`);
      if (res.ok) setUserProfile(await res.json());
    } catch {
      // The toast already told the person; the next page load picks the plan up.
    }
  }, [setUserProfile]);

  // Back from Stripe with ?checkout=success: wait for the webhook's grant.
  useEffect(() => {
    if (!user?.uid) return;
    void finishCheckoutReturn().then((active) => {
      if (active === null) return;
      if (active) {
        toast.success(translate("You are on Pro now."));
        void refreshProfile(user.uid);
      } else {
        toast(translate("Payment received. Your plan switches to Pro in a moment. Refresh in a minute if it has not."), { duration: 8000 });
      }
    });
  }, [user?.uid, refreshProfile]);

  const startUpgrade = useCallback(async (placement: UpgradePlacement) => {
    if (!user?.uid || busy) return;
    setBusy(true);
    try {
      const outcome = await startProCheckout(placement);
      if (outcome === "already_subscribed") {
        setBusy(false);
        toast(translate("You already have Pro. Manage billing from your profile page."));
      }
      // On "redirected" the page is leaving for Stripe; the button stays busy.
    } catch {
      setBusy(false);
      toast.error(translate("Checkout could not open. Check your connection and try again."));
    }
  }, [user, busy]);

  return { enabled: stripeEnabled(), busy, startUpgrade };
}
